import InfoRounded from '@mui/icons-material/InfoRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { RHFRadioField } from '@openx/components-core/lib/RadioField/RHFRadioField.tsx';
import { hasContextualFees } from '@openx/components-targeting/Targeting/ContextualSegments/utils/contextualPricing.ts';
import { type DealFormDataWithTargeting, dealFormField } from '@openx/types/deal.ts';
import type { AudiencePricing } from '@openx/types/targeting/audience.ts';
import { useFormContext } from 'react-hook-form';
import { type ContextualPricingState, NO_CONTEXTUAL_PRICING } from '../../hooks/useContextualPricingState.ts';
import { useDataPartnerFeeLogic } from '../../hooks/useDataPartnerFeeLogic.ts';
import { useDealContext } from '../../utils/context/DealContext.tsx';
import { ContextualFeesErrorAlert } from './ContextualFeesErrorAlert.tsx';
import { REVENUE_METHOD_OPTIONS } from './constants.ts';
import { PricingDetailsEdit } from './PricingDetailsEdit.tsx';

type EnabledDataPartnersProps = {
  pricingInfo: AudiencePricing | undefined;
  hasMultiProviderPricingData: boolean;
  loadingAudienceCheck: boolean;
  contextualPricing?: ContextualPricingState;
  isAdmin?: boolean;
};

export const EnabledDataPartners = ({
  pricingInfo,
  hasMultiProviderPricingData,
  loadingAudienceCheck,
  contextualPricing = NO_CONTEXTUAL_PRICING,
  isAdmin = false,
}: EnabledDataPartnersProps) => {
  const { isOa } = useDealContext();
  const { getValues, trigger } = useFormContext<DealFormDataWithTargeting>();
  const segmentPartnerRevenueMethod = getValues(dealFormField.SEGMENT_PARTNER_REVENUE_METHOD);

  const { disableRadio, infoMessage } = useDataPartnerFeeLogic({
    pricingInfo,
    hasMultiProviderPricingData,
    loadingAudienceCheck,
    contextualPricing,
  });

  const isLoading = loadingAudienceCheck || contextualPricing.isLoading;

  const hasContextualPricing = hasContextualFees(contextualPricing.pricing);
  const hasFees = hasMultiProviderPricingData || hasContextualPricing;

  const handleRevenueMethodChange = () => {
    trigger('third_party_fees_config.0.gross_share');
    trigger(dealFormField.SEGMENT_PARTNER_REVENUE_METHOD);
  };

  // audience pricing only counts for a multi-partner audience, even when contextual fees make the section enabled
  const audiencePricing = hasMultiProviderPricingData ? pricingInfo : undefined;
  const shouldShowPricingDetails = (audiencePricing || hasContextualPricing) && isOa;

  return (
    <Box
      sx={{
        mt: 1.5,
      }}
    >
      <Typography
        color={hasFees ? 'text.primary' : 'text.disabled'}
        data-test="oa-access-partner-heading"
        variant={'h3'}
      >
        {isLoading && <Loader size={16} sx={{ ml: 1 }} />}

        {contextualPricing.isFailed ? <ContextualFeesErrorAlert /> : null}

        {hasFees && !isLoading ? (
          <>
            <Stack
              direction="row"
              sx={{
                justifyContent: 'flex-start',
                alignItems: 'top',
                mt: 0.5,
              }}
            >
              <RHFRadioField
                data-test="revenue-method"
                name={dealFormField.SEGMENT_PARTNER_REVENUE_METHOD}
                options={REVENUE_METHOD_OPTIONS}
                row
                sx={{
                  marginTop: 0.5,
                }}
                isDisabled={disableRadio}
                onChangeComplete={handleRevenueMethodChange}
              />

              {infoMessage && (
                <Tooltip placement="right" title={infoMessage} sx={{ marginTop: '5px' }}>
                  <InfoRounded
                    fontSize="small"
                    sx={{
                      color: 'text.secondary',
                      ml: 0.5,
                      transform: `translateY(10px)`,
                    }}
                  />
                </Tooltip>
              )}
            </Stack>

            {shouldShowPricingDetails && (
              <PricingDetailsEdit
                {...audiencePricing}
                contextualPricing={contextualPricing.pricing}
                segmentPartnerRevenueMethod={segmentPartnerRevenueMethod}
                isAdmin={isAdmin}
              />
            )}
          </>
        ) : (
          // while loading, or with failed contextual fees, it is unknown whether the section applies
          !isLoading &&
          !contextualPricing.isFailed && (
            <Alert severity="info" data-test="enabled-fee-partners-info-alert" sx={{ mt: 2 }}>
              This section is enabled only when a Multi-Partner audience has been added.
            </Alert>
          )
        )}
      </Typography>
    </Box>
  );
};
